import React, { useEffect } from "react";
import { saveAs } from "file-saver";

import {
  FontWeights,
  getTheme,
  IButtonStyles,
  IconButton,
  IIconProps,
  IScrollablePaneStyles,
  mergeStyleSets,
  Modal,
  ScrollablePane,
} from "@fluentui/react";
import { ElaborationMessage } from "../../redux/feature/object/remote-elaborations-slice";
import { MessageDetailsCard } from "./MessageDetailsCard";
import { Checkbox } from "@progress/kendo-react-inputs";
import FooterPanel from "../layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { formatMessage, tryFormatMessageWithJson } from "../../utils/helpers/LanguageHelper";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import NotificationManager from "../../common/notification/NotificationManager";
import { useAppSelector } from "../../redux/hooks/hooks";
import { selectTenantId } from "../../redux/feature/settings/configurations-slice";
const theme = getTheme();
const contentStyles = mergeStyleSets({
  header: [
    theme.fonts.xLarge,
    {
      flex: "1 1 auto",
      borderTop: `4px solid ${theme.palette.themePrimary}`,
      color: theme.palette.neutralPrimary,
      display: "flex",
      alignItems: "center",
      fontWeight: FontWeights.semibold,
      padding: "5px 8px 5px 8px",
      borderBottom: `0.13rem solid ${"var(--color-blue)"}`,
    },
  ],
  heading: {
    color: theme.palette.neutralPrimary,
    fontWeight: FontWeights.semibold,
    fontSize: "inherit",
    margin: "0",
  },
  body: {
    flex: "4 4 auto",
    padding: "0 24px 24px 24px",
    overflowY: "hidden",
    selectors: {
      p: { margin: "14px 0" },
      "p:first-child": { marginTop: 0 },
      "p:last-child": { marginBottom: 0 },
    },
  },
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
  },
});

const iconButtonStyles: Partial<IButtonStyles> = {
  root: {
    color: theme.palette.neutralPrimary,
    marginLeft: "auto",
    marginTop: "4px",
    marginRight: "2px",
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

export interface MessageDetailsProps {
  details: ElaborationMessage[];
  isModalOpen: boolean;
  onHide: any;
}

const checkboxStyles = {
  marginRight: "5px",
};

const classNames = mergeStyleSets({
  pane: {
    height: "70vh",
    padding: "5px",
    margin: "5px",
  },
});

export interface MessageElaborationCard {
  key: string;
  text: string;
  details: string;
  severity: string;
  selected: boolean;
  traceId: string;
}

export interface MessageDetailsModalState {
  items: MessageElaborationCard[];
  selected: MessageElaborationCard[];
  allSelected: boolean;
}

const scrollablePaneStyles: Partial<IScrollablePaneStyles> = { root: classNames.pane };

export const MessageDetailsModal: React.FunctionComponent<MessageDetailsProps> = (props) => {
  const { details, isModalOpen } = props;
  const tenantId = useAppSelector(selectTenantId);

  const backIcon: IIconProps = { iconName: "Cancel" };

  const [state, setState] = React.useState<MessageDetailsModalState>({
    items: [],
    selected: [],
    allSelected: false,
  });

  useEffect(() => {
    const items = details.map((notification, index) => {
      return {
        key: `message-${index}`,
        text: notification.text,
        details: tryFormatMessageWithJson(notification.details),
        severity: notification.severity,
        selected: false,
        traceId: notification.traceId,
      };
    });

    setState((prevState) => ({ ...prevState, items, selected: [], allSelected: false }));
  }, [details]);

  const { allSelected, items, selected } = state;

  function getStatusCheck() {
    if (allSelected) return true;
    if (state.selected?.length === items.length) return true;
    if (state.selected?.length > 0) return null;
    return false;
  }

  function handleSelectAll() {
    const selected = getStatusCheck() == null ? true : !getStatusCheck();
    setState((prevState) => {
      const updatedItems = prevState.items.map((i) => ({ ...i, selected }));
      return {
        selected: selected ? [...updatedItems] : [],
        items: updatedItems,
        allSelected: selected,
      };
    });
  }

  function checkItem(item: MessageElaborationCard, sel: boolean) {
    setState((prevState) => {
      const alreadySelected = prevState.selected.some((el) => el.key === item.key);
      if (sel && alreadySelected) {
        return prevState;
      }
      const updatedItems = prevState.items.map((i) => (i.key === item.key ? { ...i, selected: sel } : i));
      const updatedSelected = sel
        ? [...prevState.selected, item]
        : prevState.selected.filter((i) => i.key !== item.key);
      return {
        selected: updatedSelected,
        items: updatedItems,
        allSelected: sel ? prevState.items.every((i) => i.selected) : false,
      };
    });
  }

  function copyToClipboard() {
    const json = generateJSON();
    if (json) {
      // eslint-disable-next-line no-undef
      navigator.clipboard.writeText(json);
      NotificationManager.addNotification({
        title: formatMessage("cof.elaboration.insertobject.details.copy.tile.messagescopiedcorrectly"),
        message: formatMessage("cof.elaboration.insertobject.details.copy.message.messagescopiedcorrectly"),
        key: "COPY_JSON_ERROR_MESSAGES",
        type: "success",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
  }

  function downloadJson() {
    const json = generateJSON();
    if (json) {
      const blob = new Blob([json], { type: "application/json" });
      saveAs(blob, "export_errors.json");
    }
  }

  function generateJSON() {
    if (selected?.length > 0) {
      const toExport = selected.map((item) => {
        return {
          traceId: item.traceId ?? "N.D.",
          tenantId: tenantId,
          objectId: item.text,
          message: item.details,
        };
      });
      return JSON.stringify(toExport);
    } else {
      NotificationManager.addNotification({
        title: formatMessage("cof.objects.modal.messages.action.noselected.title"),
        message: formatMessage("cof.objects.modal.messages.action.noselected.message"),
        key: "NOSELECTED_JSON_ERROR_MESSAGES",
        type: "warning",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
  }

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={isModalOpen}
      onDismiss={() => props.onHide()}
      isBlocking={false}
      containerClassName={contentStyles.container}
      styles={{
        main: {
          minHeight: "100%",
          minWidth: "100%",
        },
      }}
    >
      <div className={contentStyles.header}>
        <h3 className={contentStyles.heading}>
          {formatMessage("cof.base.details")} ({items ? items.length : 0})
        </h3>
        <IconButton
          styles={iconButtonStyles}
          iconProps={backIcon}
          ariaLabel="Close popup modal"
          onClick={() => props.onHide()}
        />
      </div>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <p style={{ margin: "5px" }}>{formatMessage("cof.elaboration.insertobject.details.info")}</p>
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <Checkbox
              checked={getStatusCheck()}
              size="large"
              rounded="small"
              onChange={() => handleSelectAll()}
              style={checkboxStyles}
            />{" "}
            {formatMessage("cof.base.selectall")}
          </div>
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ScrollablePane scrollContainerFocus={true} styles={scrollablePaneStyles}>
              {items.map((msg, index) => (
                <MessageDetailsCard message={msg} index={index} key={`detailsCard-${index}`} onCheck={checkItem} />
              ))}
            </ScrollablePane>
          </div>
        </div>
      </div>
      <FooterPanel>
        {selected?.length > 0 && (
          <PrimaryButton
            className={ButtonVariants.white}
            text={formatMessage("cof.base.exportlist")}
            onClick={downloadJson}
            style={{ float: "left" }}
            iconProps={{ iconName: "Installation" }}
            disabled={selected?.length == 0}
          />
        )}
        <PrimaryButton
          text={formatMessage("cof.base.copy")}
          onClick={copyToClipboard}
          style={{ float: "right" }}
          disabled={selected?.length == 0}
        />
      </FooterPanel>
    </Modal>
  );
};
